import { useCallback, useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { type PaymentMethodCard } from '@sg/graphql-schema';

import {
  type FetchPaymentMethodsParams,
  type FetchPaymentMethodsResponse,
  type PaymentMethod,
} from '@order/features-ordering';

import {
  BagPaymentMethodsDocument,
  type BagPaymentMethodsQuery,
} from '../../GraphQL/BagPaymentMethods.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the customer's payment methods.
 */
export const usePaymentMethods = () => {
  const client = useClient();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const paymentMethodsQuery = useCallback(async () => {
    const queryPaymentMethods = client.query<BagPaymentMethodsQuery>;

    return queryPaymentMethods(
      BagPaymentMethodsDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();
  }, [client.query]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const fetchPaymentMethods = useCallback(async () => {
    const response = await paymentMethodsQuery();
    const paymentMethods = response.data?.paymentMethodCards ?? [];

    return paymentMethods.map((paymentMethod) => {
      if (!isPaymentMethodCard(paymentMethod)) {
        throw new Error('Invalid payment method');
      }

      return convertPaymentMethod(paymentMethod);
    });
  }, [paymentMethodsQuery]);

  const fetchPaymentMethodsPromise = useMemo(
    () =>
      fromPromise<FetchPaymentMethodsResponse, FetchPaymentMethodsParams>(async () => ({
        paymentMethods: await fetchPaymentMethods(),
      })),
    [fetchPaymentMethods],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchPaymentMethods, fetchPaymentMethodsPromise };
};

const isPaymentMethodCard = (
  paymentMethod: BagPaymentMethodsQuery['paymentMethodCards'][number],
): paymentMethod is Extract<
  BagPaymentMethodsQuery['paymentMethodCards'][number],
  { __typename: 'PaymentMethodCard' }
> => '__typename' in paymentMethod && paymentMethod.__typename === 'PaymentMethodCard';

const convertPaymentMethod = (paymentMethod: PaymentMethodCard) => {
  const { cardType, expirationState: exp } = paymentMethod;

  return {
    ...paymentMethod,
    cardType: cardType as unknown as PaymentMethod['cardType'],
    expirationState: exp as unknown as PaymentMethod['expirationState'],
  };
};
